import * as React from "react";
import {OnFailure, OnSuccess, OnSuccessData} from "../../store/action/actionHelpers";
import Appointment from "../../model/appointment/Appointment";
import {option, Option} from "ts-option";
import {Dispatch} from "redux";
import {createAppointment, fetchLeadAppointments, updateAppointment} from "../../store/action/appointment/appointment";
import {connect} from "react-redux";
import {RootState} from "../../store/reducer/root/RootState";
import Toast from "../helper/Toast";
import AppointmentFormView from "./AppointmentFormView";

interface ComponentProps {
	/**
	 * Lead identifier
	 */
	leadId: Option<string>;
	/**
	 * Appointment update callback
	 * @param {Appointment} appointment
	 * @param {Option<string>} leadId
	 */
	onAppointmentUpdate?: (appointment: Appointment) => void;
	/**
	 * Appointment create callback
	 * @param {Appointment} appointment
	 * @param {Option<string>} leadId
	 */
	onAppointmentCreate?: (appointment: Appointment) => void;
	/**
	 * Array of appointments to create
	 */
	appointmentsToCreate: Option<Appointment[]>
	/**
	 * Indicates that add appointment is possible
	 */
	addAppointmentPossible?: boolean
	/**
	 * Custom class for appointments table.
	 */
	tableCustomClass?: string
	/**
	 * Lead form contact. Needed to show appointment confirmation during lead creation.
	 */
	leadFormContactId: Option<string>;
}

interface ReduxProps {
	/**
	 * Array of appointments
	 */
	appointments: Option<Appointment[]>;
}

interface DispatchProps {
	/**
	 * Fetch lead appointments
	 * @param {string} leadId
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchAppointments: (leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void

	/**
	 * Update appointment
	 * @param {string} leadId
	 * @param {Appointment} appointment
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateAppointment: (leadId: string, appointment: Appointment, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Create appointment
	 * @param {string} leadId
	 * @param {Appointment} appointment
	 * @param {OnSuccessData<Appointment>} onSuccess
	 * @param {OnFailure} onFailure
	 */
	createAppointment: (leadId: string,
		appointment: Appointment,
		onSuccess: OnSuccessData<Appointment>,
		onFailure: OnFailure) => void;
}


type Props = DispatchProps & ReduxProps & ComponentProps;

class AppointmentForm extends React.Component<Props, {}> {

	constructor(props: Props) {
		super(props);
		this.updateAppointment = this.updateAppointment.bind(this);
		this.createAppointment = this.createAppointment.bind(this);
	}

	componentDidMount() {
		this.props.leadId.map(l => this.props.fetchAppointments(l,
			() => {this.setState({isLoading: false})},
			() => {Toast.error("Sorry, something went wrong. Try again later.", "Fetch appointments")})
		)
	}

	render() {
		return <AppointmentFormView leadId={this.props.leadId}
									updateAppointment={this.updateAppointment}
									createAppointment={this.createAppointment}
									appointments={option(
										this.props.appointments.getOrElse(this.props.appointmentsToCreate.getOrElse([])))}
									addAppointmentPossible={this.props.addAppointmentPossible}
									tableCustomClass={this.props.tableCustomClass}
									leadFormContactId={this.props.leadFormContactId}/>
	}

	updateAppointment(a: Appointment, leadId: Option<string>) {
		if (leadId.isDefined) {
			this.props.updateAppointment(leadId.get, a,
				() => {Toast.success("Appointment updated", "Appointment updated")},
				() => {
					Toast.error("Sorry, something went wrong. Try again later.",
						"Update appointment")
				})
		}
		else {
			this.props.onAppointmentUpdate(a)
		}
	}

	createAppointment(a: Appointment, leadId: Option<string>) {
		if (leadId.isDefined) {
			this.props.createAppointment(leadId.get, a,
				() => {Toast.success("Appointment created", "Appointment created")},
				() => {
					Toast.error("Sorry, something went wrong. Try again later.",
						"Create appointment")
				})
		}
		else {
			this.props.onAppointmentCreate(a);
		}
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchAppointments: (leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchLeadAppointments(leadId, onSuccess, onFailure)),
		updateAppointment: (leadId: string, appointment: Appointment, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateAppointment(leadId, appointment, onSuccess, onFailure)),
		createAppointment: (leadId: string,
			appointment: Appointment,
			onSuccess: OnSuccessData<Appointment>,
			onFailure: OnFailure) => dispatch(createAppointment(leadId, appointment, onSuccess, onFailure))
	}
}

function mapStateToProps(state: RootState, props: Props) {
	return {
		appointments: props.leadId.flatMap(l => option(state.appointment.appointments[l]))
	}
}

export default connect<ReduxProps, DispatchProps, ComponentProps>(mapStateToProps, mapDispatchToProps)(AppointmentForm)